import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { OPSBELT_APP_URL } from "../lib/env";
import { useMe } from "../lib/hooks";
import { monthlyResetLabel } from "../lib/format";

// Show the upgrade nudge once usage crosses this fraction of the free limit.
const NEAR_THRESHOLD = 0.8;

/**
 * Cross-page usage CTA. On a metered tier (limit != null), warns as the monthly
 * free uploads run low and hard-flags when they're exhausted, always pointing to
 * the upgrade screen in OpsBelt (admins → billing; members → request access).
 * Renders nothing on unlimited tiers or while under the threshold.
 */
export function UsageBanner() {
  const me = useMe();
  const usage = useQuery({ queryKey: ["usage"], queryFn: () => api.usage() });

  const u = usage.data;
  if (!u || u.limit == null) return null;
  const ratio = u.limit > 0 ? u.used / u.limit : 1;
  if (ratio < NEAR_THRESHOLD) return null;

  const over = u.used >= u.limit;
  const isAdmin =
    me.data?.org.role === "owner" || me.data?.org.role === "admin";
  const reset = monthlyResetLabel();

  const tone = over
    ? "border-danger/30 bg-danger-soft text-danger"
    : "border-warn/30 bg-warn-soft text-warn";

  return (
    <div
      className={`mb-5 flex flex-col gap-2 rounded-md border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between ${tone}`}
    >
      <span>
        {over ? (
          <>
            <span className="font-semibold">
              You've used all {u.limit} free uploads this month
            </span>{" "}
            ({u.used}/{u.limit}). New uploads are blocked until they reset on{" "}
            {reset}
            {isAdmin ? "." : " — ask an organisation admin to upgrade BlobShip."}
          </>
        ) : (
          <>
            <span className="font-semibold">
              {u.used}/{u.limit} free uploads used this month
            </span>{" "}
            — resets {reset}
            {isAdmin ? "." : "."}
          </>
        )}
      </span>
      {/* Only admins can act on billing, so only they get the link. Members are
          pointed at their admins in the message above (no dead-end link). */}
      {isAdmin && (
        <a
          href={`${OPSBELT_APP_URL}/billing`}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 font-medium underline underline-offset-2 hover:no-underline"
        >
          Upgrade in OpsBelt ↗
        </a>
      )}
    </div>
  );
}
